import { useCallback, useEffect, useRef } from "react"

import type { InventoryLotEligibleQualityStatus } from "../constants"

import { type InventoryByLotItem, InventoryByLotStatus, ProductTestStatus } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface TrackedChange {
  requestedStatus: InventoryLotEligibleQualityStatus
  hasSeenPending: boolean
}

interface UseLotQualityStatusRejectionNoticeReturn {
  trackQualityStatusChange: (lot: InventoryByLotItem, requestedStatus: InventoryLotEligibleQualityStatus) => void
}

// Inventory By Lot reports a Direct Ship lot as Not Evaluated, so both mean the Direct Ship change was applied.
const REQUESTED_STATUS_TO_APPLIED_LOT_STATUSES: Record<InventoryLotEligibleQualityStatus, InventoryByLotStatus[]> = {
  [ProductTestStatus.Approved]: [InventoryByLotStatus.Approved],
  [ProductTestStatus.OnHold]: [InventoryByLotStatus.OnHold],
  [ProductTestStatus.DirectShip]: [InventoryByLotStatus.DirectShip, InventoryByLotStatus.NotEvaluated],
  [ProductTestStatus.Rejected]: [InventoryByLotStatus.Rejected],
}

const getLotKey = (lot: Pick<InventoryByLotItem, "lotNumber" | "batchNumber">): string =>
  `${lot.lotNumber}-${lot.batchNumber ?? ""}`

// Workaround: The backend does not expose an AS400 rejection. When AS400 rejects a change it puts the lot back to its
// previous status, so a lot that was Pending and ends up with another status than requested is reported as rejected.
// Only changes saved on this screen are watched, and the notice is lost on reload. Remove once the backend exposes the
// failure to the lot list.
export const useLotQualityStatusRejectionNotice = (
  inventories: InventoryByLotItem[]
): UseLotQualityStatusRejectionNoticeReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const trackedChangesRef = useRef(new Map<string, TrackedChange>())

  const trackQualityStatusChange = useCallback(
    (lot: InventoryByLotItem, requestedStatus: InventoryLotEligibleQualityStatus) => {
      trackedChangesRef.current.set(getLotKey(lot), { requestedStatus, hasSeenPending: false })
    },
    []
  )

  useEffect(() => {
    trackedChangesRef.current.forEach((change, key) => {
      const lot = inventories.find((inventory) => getLotKey(inventory) === key)
      if (!lot?.status) return

      if (lot.status === InventoryByLotStatus.Pending) {
        change.hasSeenPending = true
        return
      }

      // Until the lot was seen as Pending, the list may simply not have caught up with the change yet.
      if (!change.hasSeenPending) return

      trackedChangesRef.current.delete(key)

      if (!REQUESTED_STATUS_TO_APPLIED_LOT_STATUSES[change.requestedStatus].includes(lot.status)) {
        notify({
          type: NotificationType.Error,
          message: `AS400 did not accept the quality status change for lot ${lot.lotNumber}`,
        })
      }
    })
  }, [inventories, notify])

  return { trackQualityStatusChange }
}
